import { Component, input, OnInit, signal } from '@angular/core';
import { combineLatest, finalize, of, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { IssuesTableRowViewModel } from 'src/app/models/viewModels/issuesTableRowViewModel';
import { IssueModel, RemidiationDetails, timelineItemCitation } from 'src/app/models/api/issue.model';
import { IssuesDataService } from '../issues/issues.component.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { IntegrationModel } from 'src/app/models/api';
import { ISidepanelTab } from 'src/app/components/sidepanel-tabs/sidepanel-tabs.component';
import { MenuAction } from 'src/app/models/menuAction.model';
import { ITimelineIssueCitation, ITimelineIssueItem } from 'src/app/components/timeline-issue/timeline-issue.component';
import { getResourceCategoryColoredIconPath } from 'src/app/common/categoryUtils';
import { ResourceCategory } from 'src/app/models/api/inventoryResource.model';
import { RagCleanupDialogComponent } from 'src/app/components/rag-cleanup-dialog/rag-cleanup-dialog.component';
import { IssuesSupportedActions } from 'src/app/services/actions/issues/issuesSupportedActions';
import { ContactSOCDialogComponent } from 'src/app/components/contact-soc-dialog/contact-soc-dialog.component';
import { QuarantineUserDialogComponent } from 'src/app/components/quarantine-user-dialog/quarantine-user-dialog.component';
import { RestrictTriggerDomainsDialogComponent } from 'src/app/components/restrict-trigger-domains-dialog/restrict-trigger-domains-dialog.component';

const incidentDescription = `A vulnerability in the "Copilot Bank Details from Excel" action - combining embedded maker credentials and dynamic input - introduced a prompt injection risk in Copilot Studio. An internal user, John Doe (johndoe@company.com), likely exploited this flaw to launch a direct prompt injection attack on an AI agent, ultimately locating and exfiltrating sensitive files. By encoding the exfiltrated data to evade monitoring and bypass Purview's logging, John Doe was able to access and transfer critical information without authorization.

First Activity
May 5, 2022 12:53 AM

Last Activity
Jun 25, 2022 12:53 AM`;

@Component({
  selector: 'app-issues',
  templateUrl: './issue.component.html',
  styleUrls: ['./issue.component.scss'],
})
export class IssueComponent implements OnInit {
  issue = input.required<string>();
  integration = input.required<string>();
  viewModel = input<IssuesTableRowViewModel>();

  public isLoading = signal<boolean>(true);
  public issueModel: IssueModel;
  public integrationId: string;
  public showNoData = false;
  public integrationName: string;
  public tabs: ISidepanelTab[];
  public menuActions = signal<MenuAction[]>([]);
  public activeTabIndex = 0;
  public issueRemediations: RemidiationDetails[] = [];
  public timelineItems: ITimelineIssueItem[] = [];
  public frameworks: { [key: string]: string[] } = {};
  public incidentDescription: string = incidentDescription;

  private readonly _httpErrorHandlerService: HttpErrorHandlerService;

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _issuesDataService: IssuesDataService,
    private readonly _storeService: StoreService
  ) {}

  public ngOnInit() {
    this.init(this.issue(), this.integration(), this.viewModel());
  }

  public onTabSelect(index: number) {
    this.activeTabIndex = index;
  }

  public actionSelected(actionString: string) {
    console.log('Action selected:', actionString);
    if (actionString === IssuesSupportedActions.excludeFromRAG) {
      this._matDialog.open(RagCleanupDialogComponent, {
        data: { files: this.issueModel.ragFiles },
        disableClose: false,
        panelClass: 'sized-popup-dialog-container',
      });
    }
    if (actionString === IssuesSupportedActions.routeToSOC) {
      this._matDialog.open(ContactSOCDialogComponent, {
        data: {
          incidentDescription: this.incidentDescription,
        },
        disableClose: false,
        panelClass: 'sized-popup-dialog-container',
      });
    }
    if (actionString === IssuesSupportedActions.restrictTriggerDomains) {
      this._matDialog.open(RestrictTriggerDomainsDialogComponent, {
        data: {},
        disableClose: false,
        panelClass: 'sized-popup-dialog-container',
      });
    }
    if (actionString === IssuesSupportedActions.quarantineUser) {
      this._matDialog.open(QuarantineUserDialogComponent, {
        data: {
          users: [
            { name: 'John Doe', email: 'contact@samplemail.com', role: 'Administrator' },
            { name: 'Alice Green', email: 'alice@mymail.com', role: 'Analyst' },
            { name: 'Ron Dove', email: 'ron@mymail.com', role: 'Analyst' },
          ],
        },
        disableClose: false,
        panelClass: 'sized-popup-dialog-container',
      });
    }
  }

  public actionClicked(action: string) {
    this.actionSelected(action);
  }

  private init(issueId: string, integrationId: string, issueView?: IssuesTableRowViewModel) {
    let issue: IssueModel | null = null;
    if (issueView) {
      issue = issueView.issue;
      this.issueRemediations = Array.isArray(issue.remediation.steps) ? issue.remediation.steps : [];
    }
    this.frameworks = issue?.frameworks && !Array.isArray(issue.frameworks) ? issue.frameworks : {};
    const issue$ = issue ? of(issue) : this._issuesDataService.getIssueByID(issueId);

    this.isLoading.set(true);

    combineLatest([issue$, this._storeService.integrations$])
      .pipe(
        take(1),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: ([issue, integrations]) => {
          this._handleDataResponse(issue, integrations);
        },
        error: (error) => {
          if (error.status === 404) this.showNoData = true;
          else this._httpErrorHandlerService.handle(error);
        },
      });
  }

  private async _handleDataResponse(issue: IssueModel, integrations: IntegrationModel[]): Promise<void> {
    const selectedIntegration: IntegrationModel | undefined = integrations.find(
      (integration) => integration.id === this.issue()
    );
    this.integrationName = selectedIntegration?.name ?? '';

    this.issueModel = issue;
    this.showNoData = false;
    this.tabs = [{ labelPath: 'issuesPage.issueOverviewTab' }, { labelPath: 'issuesPage.issueRemidationTab' }];

    if (this.issueModel?.menuActions) {
      this.menuActions.set(this.issueModel.menuActions);
    }

    this._initTimelineCardData();
  }

  private _initTimelineCardData() {
    for (const timelineItem of this.issueModel.timeline) {
      this.timelineItems.push({
        date: this._formatDate(timelineItem.timestamp),
        time: this._formatTime(timelineItem.timestamp),
        text: timelineItem.description,
        innerText: timelineItem.additionalText,
        iconPath: this._getActorIconPath(timelineItem.actor),
        citations: timelineItem.citations ? this._getTImelineItemCitations(timelineItem.citations) : [],
        tag: timelineItem.tag,
      });
    }
  }

  private _formatDate(timestamp: Date): string {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  private _formatTime(timestamp: Date): string {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private _getActorIconPath(actor: string | null): string | undefined {
    if (actor === null) {
      return undefined;
    }

    const actorIcons: Record<string, string> = {
      agent: getResourceCategoryColoredIconPath(ResourceCategory.bot),
      user: getResourceCategoryColoredIconPath(ResourceCategory.identity),
    };

    return actorIcons[actor] || undefined;
  }

  private _getTImelineItemCitations(citations: timelineItemCitation[]): ITimelineIssueCitation[] {
    const serviceToIconPathMap: Record<string, string> = {
      sharepoint: 'assets/mockData/icons/sharepoint.svg',
      outlook: 'assets/mockData/icons/outlook.svg',
      gmail: 'assets/mockData/icons/gmail.svg',
    };

    return citations.map((citation) => ({
      text: citation.attachmentName,
      iconPath: serviceToIconPathMap[citation.service],
    }));
  }
}
